import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { AuthContext } from "../../../Header/auth - context";
import { useSelector } from "react-redux";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const TIP_LABEL = { 381: "KO", 383: "KZ", 384: "PO" };

const PregledDokumenataKOKZ = ({ setIdDokument, setKoKzView }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const toast = useRef(null);
  const company = useSelector((s) => s.company);

  const [podaci, setPodaci] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzGetPregled", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((data) => setPodaci(data || []))
      .catch(() => setPodaci([]));
  }, []);

  const handleRowSelect = (row) => {
    const id = row?.id;
    setSelectedId((prev) => (String(prev) === String(id) ? null : id));
  };

  const handleOtvori = (row) => {
    if (!row) return;
    const isDinarska = row.TipFakture === 305;
    setIdDokument(row.id);
    setKoKzView(isDinarska ? 1 : 2);
  };

  const exportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("KO-KZ Dokumenti");
    const headers = [
      "Tip", "Broj dokumenta", "Klijent", "Datum", "Valuta", "SEF status", "Napomena",
    ];
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
      cell.border = {
        top: { style: "medium" }, left: { style: "medium" },
        bottom: { style: "medium" }, right: { style: "medium" },
      };
    });
    (podaci || []).forEach((r) => {
      const row = worksheet.addRow([
        TIP_LABEL[r.TipDokumenta] || r.TipDokumenta,
        r.brojDokumenta || "",
        r.NazivKlijenta || "",
        r.Datum || "",
        r.Valuta || "",
        r.sefStatus || "",
        r.napomena || "",
      ]);
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" }, left: { style: "thin" },
          bottom: { style: "thin" }, right: { style: "thin" },
        };
      });
    });
    worksheet.columns.forEach((col) => {
      let max = 10;
      col.eachCell((cell) => {
        if (cell.value) max = Math.max(max, cell.value.toString().length);
      });
      col.width = max + 2;
    });
    const dt = new Date()
      .toLocaleString("sr-RS", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
      })
      .replace(/,/g, "");
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `KO-KZ Dokumenti - ${company?.companyName || ""} - ${dt}.xlsx`
    );
  };

  const tableHeader = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Button
          type="button"
          icon="pi pi-file-excel"
          severity="success"
          rounded
          onClick={exportExcel}
          data-pr-tooltip="XLS"
        />
      </div>
      <input
        type="search"
        placeholder={t('nova.phPretraga')}
        className="p-inputtext p-component"
        onInput={(e) => setGlobalFilter(e.target.value)}
      />
    </div>
  );

  return (
    <div className="zaposleni_table_container">
      <Toast ref={toast} />
      <Tooltip target=".export-buttons>button" position="bottom" />
      <DataTable
            value={podaci || []}
            paginator
            rows={15}
            globalFilter={globalFilter}
            scrollable
            scrollHeight="60vh"
            header={tableHeader}
            resizableColumns
            columnResizeMode="expand"
            className="turePregledTable stavke-tabela"
            rowClassName={(r) =>
              String(r.id) === String(selectedId) ? { "selected-row": true } : {}
            }
          >
            <Column
              header="Otvori"
              style={{ minWidth: 80 }}
              body={(rowData) => (
                <Button
                  label="Otvori"
                  icon="pi pi-pencil"
                  type="button"
                  className="p-button-text p-button-sm"
                  onClick={() => {
                    handleRowSelect(rowData);
                    handleOtvori(rowData);
                  }}
                />
              )}
            />
            <Column
              field="TipDokumenta"
              header="Tip"
              style={{ minWidth: 60 }}
              body={(r) => TIP_LABEL[r.TipDokumenta] || r.TipDokumenta}
            />
            <Column field="brojDokumenta" header="Broj dokumenta" style={{ minWidth: 150 }} />
            <Column field="NazivKlijenta" header="Klijent" style={{ minWidth: 200 }} />
            <Column field="Datum" header="Datum" style={{ minWidth: 110 }} />
            <Column field="Valuta" header="Valuta" style={{ minWidth: 80 }} />
            <Column
              field="sefStatus"
              header="SEF status"
              style={{ minWidth: 110 }}
              body={(r) => (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 4,
                    background:
                      r.sefStatus === "Approved"
                        ? "#d4edda"
                        : r.sefStatus === "Rejected"
                        ? "#f8d7da"
                        : r.sefStatus === "Sent"
                        ? "#fff3cd"
                        : "#e9ecef",
                    color:
                      r.sefStatus === "Approved"
                        ? "#155724"
                        : r.sefStatus === "Rejected"
                        ? "#721c24"
                        : r.sefStatus === "Sent"
                        ? "#856404"
                        : "#495057",
                    fontSize: "0.8rem",
                  }}
                >
                  {r.sefStatus || "–"}
                </span>
              )}
            />
            <Column field="napomena" header="Napomena" style={{ minWidth: 180 }} />
          </DataTable>
    </div>
  );
};

export default PregledDokumenataKOKZ;
